/**
 * AppButton — the app's standard button, a thin wrapper over Fluent UI v9 Button.
 *
 * Uses Fluent's NATIVE appearances (no custom gradients/uppercase) so buttons
 * carry the real Fluent look and feel. The Fluent theme's brand color is pinned
 * to Utah red, so `appearance="primary"` is red automatically.
 *
 * Colour semantics (the point of this wrapper):
 *   - Filled red  = affirmative / primary action (primary, primary-outline).
 *   - Outline red = DESTRUCTIVE action (danger) — Fluent has no danger button,
 *     so we tint the `outline` appearance with Fluent's status-danger tokens.
 *     Filled-vs-outline keeps destructive visually distinct from affirmative.
 *   - Neutral     = secondary / cancel / discard.
 *   - Green fill  = positive completion (success); amber (warning); subtle/ghost;
 *     transparent red text (tertiary).
 *
 * Pass-through props (onClick, disabled, icon, size, etc.) match Fluent Button.
 */

import {
  Button as FluentButton,
  makeStyles,
  mergeClasses,
  tokens,
  type ButtonProps,
  type GriffelStyle,
} from '@fluentui/react-components';
import { brand, buttonGradient } from '@/shared/styles/colors';

export type AppButtonVariant =
  | 'primary'
  | 'primary-outline'
  | 'secondary'
  | 'danger'
  | 'success'
  | 'warning'
  | 'ghost'
  | 'tertiary';

export interface AppButtonProps extends Omit<ButtonProps, 'appearance' | 'as' | 'type'> {
  variant?: AppButtonVariant;
  /** Full-width button (legacy .btn-block). */
  block?: boolean;
  type?: 'button' | 'submit' | 'reset';
  /**
   * Native `form` attribute — associates a submit button with a form by id,
   * which works even when the button is portaled (e.g. in a Dialog footer).
   */
  form?: string;
}

// Griffel forbids the border shorthand, so set the four side colors.
const borderColor = (c: string): GriffelStyle => ({
  borderTopColor: c,
  borderRightColor: c,
  borderBottomColor: c,
  borderLeftColor: c,
});

// Keep custom-colored variants looking right when disabled (Fluent's disabled
// rule doesn't always beat our color overrides).
const disabledReset: GriffelStyle = {
  color: tokens.colorNeutralForegroundDisabled,
  backgroundColor: tokens.colorNeutralBackgroundDisabled,
  ...borderColor(tokens.colorNeutralStrokeDisabled),
};

const useStyles = makeStyles({
  block: { width: '100%' },

  // DESTRUCTIVE — red outline (on Fluent's `outline` appearance). Distinct from
  // the filled-red affirmative primary.
  danger: {
    color: tokens.colorStatusDangerForeground1,
    backgroundColor: tokens.colorTransparentBackground,
    ...borderColor(tokens.colorStatusDangerBorder2),
    ':hover': {
      color: tokens.colorStatusDangerForeground1,
      backgroundColor: tokens.colorStatusDangerBackground1,
      ...borderColor(tokens.colorStatusDangerBorder2),
    },
    ':hover:active': {
      color: tokens.colorStatusDangerForeground1,
      backgroundColor: tokens.colorStatusDangerBackground2,
      ...borderColor(tokens.colorStatusDangerBorder2),
    },
    ':disabled': disabledReset,
    ':disabled:hover': disabledReset,
  },

  // Positive completion — flat green fill (on `primary` appearance).
  success: {
    color: brand.white,
    backgroundColor: brand.mountainGreen,
    ...borderColor(brand.mountainGreen),
    ':hover': {
      color: brand.white,
      backgroundColor: buttonGradient.greenDark,
      ...borderColor(buttonGradient.greenDark),
    },
    ':disabled': disabledReset,
    ':disabled:hover': disabledReset,
  },

  // Caution — flat amber fill (on `primary` appearance).
  warning: {
    color: brand.bodyText,
    backgroundColor: brand.wasatchSunrise,
    ...borderColor(brand.wasatchSunrise),
    ':hover': {
      color: brand.bodyText,
      backgroundColor: buttonGradient.yellowDark,
      ...borderColor(buttonGradient.yellowDark),
    },
    ':disabled': disabledReset,
    ':disabled:hover': disabledReset,
  },

  // Low-emphasis red text (on `transparent` appearance).
  tertiary: {
    color: tokens.colorBrandForeground1,
    ':hover': { color: tokens.colorBrandForeground1 },
  },
});

// Variant → Fluent native appearance.
const APPEARANCE: Record<AppButtonVariant, ButtonProps['appearance']> = {
  primary: 'primary',
  'primary-outline': 'primary',
  secondary: 'secondary',
  danger: 'outline',
  success: 'primary',
  warning: 'primary',
  ghost: 'subtle',
  tertiary: 'transparent',
};

// Only variants that need a color override beyond the native appearance.
const VARIANT_CLASS: Partial<Record<AppButtonVariant, keyof ReturnType<typeof useStyles>>> = {
  danger: 'danger',
  success: 'success',
  warning: 'warning',
  tertiary: 'tertiary',
};

export function AppButton({
  variant = 'secondary',
  block,
  size,
  className,
  children,
  ...rest
}: AppButtonProps) {
  const styles = useStyles();
  const variantKey = VARIANT_CLASS[variant];
  const variantClass = variantKey ? styles[variantKey] : undefined;

  return (
    <FluentButton
      appearance={APPEARANCE[variant]}
      size={size}
      className={mergeClasses(variantClass, block && styles.block, className)}
      {...(rest as ButtonProps)}
    >
      {children}
    </FluentButton>
  );
}

export default AppButton;
